import {Avatar, Card, type CardProps} from 'platform/components';
import {
  Box,
  HStack,
  Show,
  Text,
  type ThemeColorPath,
  type ThemeIconKey,
  VStack,
} from 'platform/foundation';
import {CurrencyFormat, DateTimeFormat} from 'platform/locale';
import {match, Pattern} from 'ts-pattern';

import type {BusinessCaseResponseBody} from '@dms/api/sales';

import {parseDate} from 'shared/web';

import {useCustomerInsightsBusinessCasePrice} from '../../../hooks/useCustomerInsightsBusinessCasePrice';
import type {
  CustomerInsightsBusinessCaseRecord,
  CustomerInsightsRecord,
} from '../../../types/CustomerInsightsViewModel';
import {CustomerInsightsClickableRow} from './CustomerInsightsClickableRow';
import {CustomerInsightsLink} from './CustomerInsightsLink';
import {CustomerInsightsRecordFlags} from './CustomerInsightsRecordFlags';

interface CustomerInsightsRecordListItemProps {
  record: CustomerInsightsRecord;
  canReadTags: boolean;
}

interface CustomerInsightsRecordCardProps extends CustomerInsightsRecordListItemProps {
  businessCase?: BusinessCaseResponseBody;
  businessCaseEnrichment?: {
    isLoading: boolean;
    isError: boolean;
    retry: VoidFunction;
  };
  canReadPrice?: boolean;
}

interface CustomerInsightsBusinessCaseRecordListItemProps {
  record: CustomerInsightsBusinessCaseRecord;
  canReadTags: boolean;
}

const renderRecordTitle = (record: CustomerInsightsRecord) =>
  match(record.href)
    .with(Pattern.string, (href) => (
      <CustomerInsightsLink
        href={href}
        title={record.title}
        size="xSmall"
        overflowWrap="anywhere"
      />
    ))
    .otherwise(() => (
      <Text size="small" alternative overflowWrap="anywhere">
        {record.title}
      </Text>
    ));

const renderOccurredAt = (occurredAt: string | undefined) =>
  match(occurredAt)
    .with(Pattern.string, (value) => {
      const date = parseDate(value);

      return match(date)
        .when(
          (parsedDate): parsedDate is Date =>
            parsedDate !== null && !Number.isNaN(parsedDate.getTime()),
          (validDate) => (
            <Text size="xSmall" color="secondary">
              <DateTimeFormat format="dateTimeShort" value={validDate} />
            </Text>
          )
        )
        .otherwise(() => null);
    })
    .otherwise(() => null);

const RECORD_STYLES: Record<
  CustomerInsightsRecord['kind'],
  {
    background: ThemeColorPath;
    color: ThemeColorPath;
    colorStripe: NonNullable<CardProps['colorStripe']>;
    icon: ThemeIconKey;
  }
> = {
  interest: {
    background: 'palettes.teal.20.100',
    color: 'palettes.teal.80.100',
    colorStripe: 'teal',
    icon: 'sidebar/interest',
  },
  businessCase: {
    background: 'palettes.blue.20.100',
    color: 'palettes.blue.80.100',
    colorStripe: 'blue',
    icon: 'sidebar/business_case',
  },
  serviceCase: {
    background: 'palettes.teal.20.100',
    color: 'palettes.teal.80.100',
    colorStripe: 'teal',
    icon: 'action/build',
  },
};

const renderRecordCode = (record: CustomerInsightsRecord) =>
  match({
    code: record.code,
    title: record.title,
    kind: record.kind,
    href: record.href,
  })
    .with({code: Pattern.nullish}, () => null)
    .with({code: ''}, () => null)
    .with({kind: 'interest'}, () => null)
    .with({kind: 'businessCase'}, () => null)
    .when(
      ({code, title}) => code === title,
      () => null
    )
    .with({code: Pattern.string, href: Pattern.string}, ({code, href}) => (
      <CustomerInsightsLink href={href} title={code} size="xSmall" overflowWrap="anywhere" />
    ))
    .otherwise(({code}) => (
      <Text size="xSmall" color="secondary" overflowWrap="anywhere">
        {code}
      </Text>
    ));

const renderRecordPrice = (record: CustomerInsightsRecord, isPriceVisible: boolean) => {
  const price = match(record)
    .with({kind: 'businessCase'}, (businessCaseRecord) => businessCaseRecord.price)
    .otherwise(() => undefined);

  return (
    <Show when={price && isPriceVisible}>
      <Text
        size="small"
        alternative
        noWrap
        data-testid={`customerInsights-record-${record.id}-price`}
      >
        <CurrencyFormat number={price?.amount ?? 0} currency={price?.currency ?? ''} />
      </Text>
    </Show>
  );
};

export function CustomerInsightsRecordListItem(props: CustomerInsightsRecordListItemProps) {
  return match(props.record)
    .with({kind: 'businessCase'}, (record) => (
      <CustomerInsightsBusinessCaseRecordListItem record={record} canReadTags={props.canReadTags} />
    ))
    .otherwise((record) => (
      <CustomerInsightsRecordCard record={record} canReadTags={props.canReadTags} />
    ));
}

function CustomerInsightsBusinessCaseRecordListItem(
  props: CustomerInsightsBusinessCaseRecordListItemProps
) {
  const {
    price: businessCasePrice,
    canReadPrice,
    businessCase,
    isLoading: isBusinessCasePriceLoading,
    isError: isBusinessCasePriceError,
    retry: retryBusinessCasePrice,
  } = useCustomerInsightsBusinessCasePrice(props.record);

  return (
    <CustomerInsightsRecordCard
      record={{...props.record, price: businessCasePrice}}
      canReadTags={props.canReadTags}
      canReadPrice={canReadPrice}
      businessCase={businessCase}
      businessCaseEnrichment={{
        isLoading: isBusinessCasePriceLoading,
        isError: isBusinessCasePriceError,
        retry: retryBusinessCasePrice,
      }}
    />
  );
}

function CustomerInsightsRecordCard(props: CustomerInsightsRecordCardProps) {
  const isPriceVisible = props.record.kind !== 'businessCase' || props.canReadPrice === true;
  const style = RECORD_STYLES[props.record.kind];

  const recordDetails = (
    <>
      <HStack spacing={2} align="flex-start" justify="space-between" minWidth={0}>
        <Box minWidth={0} flex={1}>
          {renderRecordTitle(props.record)}
        </Box>
        {renderRecordPrice(props.record, isPriceVisible)}
      </HStack>
      <Show when={props.record.subtitle}>
        <Text size="xSmall" color="secondary" overflowWrap="anywhere">
          {props.record.subtitle}
        </Text>
      </Show>
      <HStack spacing={2} align="center" wrap>
        {renderRecordCode(props.record)}
        <Show when={props.record.owner}>
          <Text size="xSmall" color="secondary" overflowWrap="anywhere">
            {props.record.owner}
          </Text>
        </Show>
        {renderOccurredAt(props.record.occurredAt)}
      </HStack>
    </>
  );

  return (
    <CustomerInsightsClickableRow href={props.record.href}>
      <Card
        variant="inlineWhite"
        colorStripe={style.colorStripe}
        data-testid={`customerInsights-record-${props.record.id}`}
      >
        <Box role="listitem" minWidth={0}>
          <HStack spacing={3} align="flex-start" minWidth={0}>
            <Avatar
              size="default"
              variant="square"
              icon={style.icon}
              color={style.background}
              iconColor={style.color}
            />
            <VStack spacing={1} minWidth={0} grow={1}>
              <CustomerInsightsRecordFlags
                record={props.record}
                canReadTags={props.canReadTags}
                businessCase={props.businessCase}
                businessCaseEnrichment={props.businessCaseEnrichment}
              />
              <VStack spacing={1} minWidth={0}>
                {recordDetails}
              </VStack>
            </VStack>
          </HStack>
        </Box>
      </Card>
    </CustomerInsightsClickableRow>
  );
}
